import type { ArrayType } from '../../../../../../utils'

import { areAgentEquals, Capability, type CybusAgent, StatusType } from '../../../../../../domain'

import type { BackendJsonResponseContent } from '../../../../../Connectware'
import { isLastPage } from '../../../mappers'
import { BaseSubscriptionStrategy } from '../Base'

type AgentData = ArrayType<BackendJsonResponseContent<'/api/v2/resource-runners', 'GET', 200>['data']>

export class AgentsStrategy extends BaseSubscriptionStrategy<'agents', never> {
    /**
     * Agents that should not be considered for listing
     */
    private static readonly rejectAgents = new Set(['auth-server', 'service-manager', 'system-control-server', 'container-manager'])

    protected override readonly areEquals = areAgentEquals

    override readonly requiredPermissions = [Capability.AGENTS_READ]

    private static mapAgentData ({ name, version, hostname, healthy }: AgentData): CybusAgent | null {
        if (this.rejectAgents.has(name)) {
            return null
        }

        const nullifiedHostname = hostname || null

        // If agent container was started without hostname argument, we get
        // just the Docker container ID here, which is somewhat confusing.
        const isHostnameContainerId = nullifiedHostname && /^[0-9a-f]{12}$/.exec(nullifiedHostname)

        return {
            name,
            version,
            hostname: isHostnameContainerId ? null : nullifiedHostname,
            containerId: isHostnameContainerId ? nullifiedHostname : null,
            status: healthy ? StatusType.ONLINE : StatusType.OFFLINE,
        }
    }

    override async retrieveAll (): Promise<CybusAgent[]> {
        const agents: CybusAgent[] = []
        let isDone = false
        let page = 1

        while (!isDone) {
            isDone = await this.request<'/api/v2/resource-runners', 'GET', boolean>({
                capability: Capability.AGENTS_READ,
                method: 'GET',
                path: '/api/v2/resource-runners',
                authenticate: true,
                queryParams: { page: `${page}` },
                handlers: {
                    200: (response) =>
                        response.getJson().then(({ data, meta }) => {
                            for (const info of data) {
                                const agent = AgentsStrategy.mapAgentData(info)

                                if (agent) {
                                    agents.push(agent)
                                }
                            }

                            return isLastPage(meta.pagination)
                        }),
                    503: AgentsStrategy.createServerNotAvailableHandler('Resources Status Tracking is not running as expected'),
                },
            })

            page++
        }

        return agents
    }
}
